'use client';
import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { Button, Input, SectionHeader } from '@/components/ui';
import { IconMap, IconSearch } from '@repo/svgs';
import { useStore } from '@/providers/StoreProvider';
import LocationResults from './LocationResults';
import SelectedNeighborhoodChips from './components/SelectedNeighborhoodChips';
import NeighborhoodAccordion from './components/NeighborhoodAccordion';
import { useAnalytics } from '@/hooks/useAnalytics';
import SuggestedLocationsSection from './components/SuggestedLocations';

export interface LocationFilterContentProps {
  searchQuery: string;
  onSearchQueryChange?: (query: string) => void;
  variant: 'mobile' | 'desktop';
  onViewMap: () => void;
  showSearchInput?: boolean;
  searchPlaceholder?: string;
  showSuggestedLocations?: boolean;
  showBuildingSuggestions?: boolean;
  selectionMode?: 'store' | 'external';
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  className?: string;
}

export function LocationFilterContent({
  searchQuery,
  onSearchQueryChange,
  variant,
  onViewMap,
  showSearchInput = false,
  searchPlaceholder = 'Search neighborhood, address, or building',
  showSuggestedLocations = true,
  showBuildingSuggestions = true,
  selectionMode = 'store',
  selectedNeighborhoodIds,
  onToggleNeighborhood,
  onToggleNeighborhoodWithChildren,
  onToggleBorough,
  className,
}: LocationFilterContentProps) {
  const isMobile = variant === 'mobile';
  const analytics = useAnalytics();
  const hasTrackedSearchRef = useRef(false);
  const building = useStore(s => s.listingsFilters.building);
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const resetNeighborhoods = useStore(s => s.listingsFilters.resetNeighborhoods);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length === 0) {
      hasTrackedSearchRef.current = false;
      return;
    }
    if (hasTrackedSearchRef.current) return;
    hasTrackedSearchRef.current = true;
    analytics.track('listings_filter_neighborhood_search');
  }, [analytics, searchQuery]);

  useEffect(() => {
    if (selectionMode !== 'store') return;
    if (neighborhoodIds.length > 0) {
      resetBuilding();
    }
  }, [selectionMode, neighborhoodIds, resetBuilding]);

  useEffect(() => {
    if (selectionMode !== 'store') return;
    if (building?.slug) {
      resetNeighborhoods();
    }
  }, [selectionMode, building?.slug, resetNeighborhoods]);

  return (
    <div className={cn('flex flex-col py-2 px-4', className)}>
      {/* Optional search input (mobile only) */}
      {showSearchInput && onSearchQueryChange && (
        <Input
          value={searchQuery}
          leftIcon={<IconSearch className="w-4 h-4" />}
          onChange={e => onSearchQueryChange(e.target.value)}
          placeholder={searchPlaceholder}
        />
      )}

      {searchQuery.length === 0 && (
        <div className="px-2">
          <SelectedNeighborhoodChips
            mobile={isMobile}
            includeBuilding={selectionMode === 'store'}
            className="-mr-6 md:-mr-0 py-4 border-b border-tertiary !flex-wrap"
            {...(selectionMode === 'external'
              ? {
                  selectedNeighborhoodIds: selectedNeighborhoodIds ?? [],
                  onToggleNeighborhood,
                  onToggleNeighborhoodWithChildren,
                  onToggleBorough,
                }
              : {})}
          />
        </div>
      )}

      {/* Search results or browse mode */}
      {searchQuery.length > 0 ? (
        <div className="flex-1">
          <LocationResults
            searchQuery={searchQuery}
            variant={variant}
            showBuildingSuggestions={showBuildingSuggestions}
            {...(selectionMode === 'external'
              ? {
                  selectedNeighborhoodIds: selectedNeighborhoodIds ?? [],
                  onToggleNeighborhood,
                  onToggleNeighborhoodWithChildren,
                  onToggleBorough,
                  onSelectResult: () => onSearchQueryChange?.(''),
                }
              : {})}
          />
        </div>
      ) : (
        <div className={cn('flex-1 space-y-4', isMobile ? 'overflow-y-auto' : '')}>
          {isMobile ? (
            <>
              {showSuggestedLocations && <SuggestedLocationsSection />}

              <div className="flex items-center justify-between mb-2">
                <SectionHeader title="All neighborhoods" />
                <button
                  onClick={() => {
                    analytics.track('listings_filter_allneighborhoods_map_seeall');
                    onViewMap();
                  }}
                  className="text-sm text-accent cursor-pointer hover:opacity-70 transition-opacity px-2"
                >
                  View map
                </button>
              </div>
              <NeighborhoodAccordion
                resetBuildingOnSelect={selectionMode === 'store'}
                {...(selectionMode === 'external'
                  ? {
                      selectedNeighborhoodIds: selectedNeighborhoodIds ?? [],
                      onToggleNeighborhood,
                      onToggleNeighborhoodWithChildren,
                      onToggleBorough,
                    }
                  : {})}
              />
            </>
          ) : (
            <>
              {showSuggestedLocations && <SuggestedLocationsSection />}

              <Button
                variant="secondary"
                className="flex w-full gap-2 items-center justify-center cursor-pointer"
                onClick={e => {
                  e.stopPropagation();
                  analytics.track('listings_filter_allneighborhoods_map_seeall');
                  onViewMap();
                }}
              >
                <IconMap className="w-4 h-4" />
                <span className="text-body-2-high">View all neighborhoods</span>
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default LocationFilterContent;
